import {OAuthRequestParams} from "../../../../shared/oauth2-client/interfaces";
import Device from "../../../../shared/device";
import {
    allowedOAuthErrors,
    AuthenticationCallbackResponse,
    ChallengeVerificationOidcRequest, ChallengeVerificationRequest,
    LoginOidcRequest, LoginRequest,
    OAuthResponse, NoContentResponse,
    FailureContentResponse
} from "../model";
import {v4 as uuidv4} from 'uuid';
import {withTimeout} from "../../../../shared/timeout";
import parseQuery from "../../../../shared/url/url";
import axios from "axios"

const PRU_REQUEST_CORRELATION_HEADER = 'X-PruRequestID';
const CONTENT_TYPE_HEADER = 'Content-Type';
const FOREGROCK_TYPE = "FORGEROCK";
const PWD_PROTOCOL = "PWD"

class Auth {

    static async loginWithOidc(templateId: string, ou: string, username: string, pw: any, headers: any, oAuthRequestParams?: OAuthRequestParams, clientId?: string): Promise<AuthenticationCallbackResponse | OAuthResponse | NoContentResponse> {

        const device = new Device();
        const deviceProfile = await device.getProfile({
            location: true,
            metadata: true,
        });

        let requestPayload: LoginOidcRequest | LoginRequest = {
            "templateId": templateId,
            'ou': ou,
            'loginId': username,
            'password': pw,
            'deviceProfile': deviceProfile
        };

        if (oAuthRequestParams) {
            requestPayload = {
                ...requestPayload,
                "oauth": {
                    "clientId": oAuthRequestParams.client_id,
                    "redirectUri": oAuthRequestParams.redirect_uri,
                    "responseType": oAuthRequestParams.response_type,
                    "scope": oAuthRequestParams.scope,
                    "decision": "allow",
                    "state": oAuthRequestParams.state,
                    "codeChallenge": oAuthRequestParams.code_challenge,
                    "codeChallengeMethod": oAuthRequestParams.code_challenge_method
                }
            }
        } else if(clientId) {
            requestPayload = {
                ...requestPayload,
                "clientId": clientId
            }
        }

        const requestHeaders = this.getRequestHeaders(headers, 'application/json');
        const options: RequestInit = {
            method: 'POST',
            body: JSON.stringify(requestPayload),
            headers: requestHeaders
        }

        let response: Response = await withTimeout(fetch('/authx/gt/v1/iam/login', options));
        return await this.handleResponse(response);

    }

    static async login(templateId: string, ou: string, username: string, pw: any, headers: any, clientId: string): Promise<AuthenticationCallbackResponse | OAuthResponse | NoContentResponse> {

        const device = new Device();
        const deviceProfile = await device.getProfile({
            location: true,
            metadata: true,
        });

        let requestPayload: LoginOidcRequest | LoginRequest = {
            "templateId": templateId,
            "clientId": clientId,
            'ou': ou,
            'loginId': username,
            'password': pw,
            'deviceProfile': deviceProfile
        };

        const requestHeaders = this.getRequestHeaders(headers, 'application/json');
        const options: RequestInit = {
            method: 'POST',
            body: JSON.stringify(requestPayload),
            headers: requestHeaders
        }

        let response: Response = await withTimeout(fetch('/authx/gt/v1/iam/login', options));
        return await this.handleResponse(response);

    }

    static async changePasswordExpired(templateId: string, clientId: string, idpAuthJourneyStepId: string, pw: any, headers: any) {

        const requestPayload = {
            "templateId": templateId,
            "idpAuthJourneyStepId": idpAuthJourneyStepId,
            "clientId": clientId,
            "data": {
                "credentials": {
                    "password": pw,
                    "provider": {
                        "type": FOREGROCK_TYPE,
                        "protocol": PWD_PROTOCOL
                    }
                }
            }
        };

        const requestHeaders = this.getRequestHeaders(headers,'application/json');

        const options: RequestInit = {
            method: 'POST',
            body: JSON.stringify(requestPayload),
            headers: requestHeaders
        }

        let response = await withTimeout(fetch('/authx/gt/v1/iam/auth/user/selfService/recovery', options));

        return this.handleResponse(response);


    }

    static async generateChallenge(templateId: string, clientId: string, idpAuthJourneyStepId: string, contact: any, headers: any) {

        const requestPayload = {
            "templateId": templateId,
            "idpAuthJourneyStepId": idpAuthJourneyStepId,
            "clientId": clientId,
            "type": "otp",
            "contact": {
                "id": contact.id,
                "type": contact.type
            }
        };

        const requestHeaders = this.getRequestHeaders(headers,'application/json');

        const options: RequestInit = {
            method: 'POST',
            body: JSON.stringify(requestPayload),
            headers: requestHeaders
        }

        let response = await withTimeout(fetch('/authx/gt/v1/iam/auth/challenge', options));

        return this.handleResponse(response);


    }

    static async validateChallenge(templateId: string, idpAuthJourneyStepId: string, otpValue: string, trustDevice: boolean, headers: any, oAuthRequestParams?: OAuthRequestParams, clientId?: string) {

        let requestPayload: ChallengeVerificationOidcRequest | ChallengeVerificationRequest = {
            "templateId": templateId,
            "idpAuthJourneyStepId": idpAuthJourneyStepId,
            "type": "otp",
            "value": otpValue,
            "trustDevice": trustDevice
        };

        if (oAuthRequestParams) {
            requestPayload = {
                ...requestPayload,
                "oauth": {
                    "clientId": oAuthRequestParams.client_id,
                    "redirectUri": oAuthRequestParams.redirect_uri,
                    "responseType": oAuthRequestParams.response_type,
                    "scope": oAuthRequestParams.scope,
                    "decision": "allow",
                    "state": oAuthRequestParams.state,
                    "codeChallenge": oAuthRequestParams.code_challenge,
                    "codeChallengeMethod": oAuthRequestParams.code_challenge_method
                }
            }
        } else if(clientId) {
            requestPayload = {
                ...requestPayload,
                "clientId": clientId
            }
        }

        const requestHeaders = this.getRequestHeaders(headers, 'application/json');

        const options: RequestInit = {
            method: 'POST',
            body: JSON.stringify(requestPayload),
            headers: requestHeaders
        }

        let response = await withTimeout(fetch('/authx/gt/v1/iam/auth/challenge/validate', options));

        return this.handleResponse(response);

    }

    static async handleResponse(response: Response): Promise<AuthenticationCallbackResponse | OAuthResponse | NoContentResponse> {
        if (response.url.includes('code')) {

            let parsedUrl = new URL(response.url);

            // Throw if we have an error param or have no authorization code
            if (parsedUrl.searchParams.get('error')) {
                throw Error(`${parsedUrl.searchParams.get('error_description')}`);
            } else if (!parsedUrl.searchParams.get('code')) {
                throw Error(allowedOAuthErrors.AuthenticationConsentRequired);
            }

            const parsedQuery = parseQuery(parsedUrl.toString());

            return {
                data: {
                    oauth: parsedQuery
                }
            } as OAuthResponse;
        } else if (response.status === 200) {

            const respJson = await response.json();
            return {
                status: 200,
                data: respJson
            } as AuthenticationCallbackResponse;

        } else if (response.status === 204) {

            return {
                status: 204
            } as NoContentResponse;

        } else if (response.status === 401) {
            const respJson = await response.json();
            return {
                status: response.status,
                data: respJson
            } as FailureContentResponse;

        } else {

            const respJson = await response.json();
            throw Error(respJson.detail);

        }
    }

    static getRequestHeaders(headers: any, contentType?: any) {
        const requestHeaders = {
            ...headers
        };
        if (contentType) {
            requestHeaders[CONTENT_TYPE_HEADER] = contentType;
        }
        requestHeaders[PRU_REQUEST_CORRELATION_HEADER] = uuidv4();

        return requestHeaders;
    }

    static async exchangeOauthCodeForTokens(oauthCode: string, codeVerifier: string, oAuthRequestParams: OAuthRequestParams, headers: any) {

        const requestBody = {
            "grant_type": "authorization_code",
            "code": oauthCode,
            "client_id": oAuthRequestParams.client_id,
            "redirect_uri": oAuthRequestParams.redirect_uri,
            "code_verifier": codeVerifier
        };

        const requestHeaders = this.getRequestHeaders(headers,'application/x-www-form-urlencoded');
        return await axios.post('/authx/gt/v1/iam/oauth/token', requestBody, {
            headers: requestHeaders
        });

    }

}

export default Auth;